/**
 * Practice tab — browse chapters and assign them, plus the list of what has
 * already been created. WP6 rebuild:
 *  - Learn/Children page chrome (white canvas, title in flow) replacing the
 *    grey canvas + bordered header band, and the DS type scale throughout.
 *  - New/Created runs on the student app's league segmented control.
 *  - Syllabus chips reordered KSSM · IGCSE · KSSR (KSSR stays selectable
 *    here — only the Exam tab locks it, PEXM-014).
 *  - The download-language popover is now the shared LanguageSheet.
 *  - Re-assign: a chapter the child has FINISHED can be given again
 *    (`repeatWhenDone` on AssignSheet); one still pending or in progress
 *    stays blocked. User decision 2026-08-06.
 */

import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { useState, useEffect } from 'react';
import { getChapters } from '@/lib/api';
import { useStuck } from '@/lib/use-stuck';
import GateNoticeModal from '@/components/gate-notice-modal';
import AssignSheet from '@/components/assign-sheet';
import LanguageSheet from '@/components/language-sheet';
import ChapterSheet from '@/components/chapter-sheet';
import PickerField from '@/components/picker-field';
import TrialBanner from '@/components/trial-banner';
import AssignedByChild from '@/components/assigned-by-child';
import type { AssignmentSummary } from '@/lib/api';
import { useWorksheetStore } from '@/store/worksheet-store';
import { useLinksStore } from '@/store/links-store';
import { useAssignmentsStore } from '@/store/assignments-store';
import { fmtDayMonth, levelLabel } from '@/lib/format';
import { IconChevronRight } from '@tabler/icons-react';
import { useLanguage, type AppLocale } from '@/lib/language';
import { useTranslation, type TFunction } from '@tupai/i18n/react';

export const Route = createFileRoute('/_protected/practice')({
  validateSearch: z.object({ tab: z.string().optional(), child: z.string().optional() }),
  component: PracticePage,
});

// KSSM first, matching the Exam tab (PEXM-014). KSSR is last but stays
// enabled — it has chapters, it just has no exam papers yet.
//
// Neither these nor the level names below are translated: they are the literal
// values handed to getChapters(), so a translated label would be a broken
// query, not a localised one.
const SYLLABUSES = ['KSSM', 'IGCSE', 'KSSR'] as const;

const DEFAULT_LEVEL: Record<string, string> = {
  KSSM: 'Form 1',
  IGCSE: 'Lower secondary',
  KSSR: 'Year 4',
};

function levelsFor(syllabus: string): string[] {
  if (syllabus === 'KSSR') return ['Year 4', 'Year 5', 'Year 6'];
  if (syllabus === 'KSSM') return ['Form 1', 'Form 2', 'Form 3', 'Form 4', 'Form 5'];
  return ['Lower secondary', 'Upper secondary'];
}

// One line of context under a chapter the parent has already given out, so
// the re-assign rule reads as a consequence rather than a surprise.
function assignmentNote(
  rows: AssignmentSummary[],
  t: TFunction<'work'>,
  locale: AppLocale
): string | null {
  if (rows.length === 0) return null;
  if (rows.length > 1) return t('assignedToN', { n: rows.length });
  const a = rows[0];
  if (a.status === 'completed')
    return a.completed_at
      ? t('finishedThis', {
          studentName: a.student_name,
          date: fmtDayMonth(a.completed_at, locale),
        })
      : t('finishedThisNoDate', { studentName: a.student_name });
  if (a.status === 'started') return t('workingOnThis', { studentName: a.student_name });
  return t('assignedToOn', {
    studentName: a.student_name,
    date: fmtDayMonth(a.assigned_at, locale),
  });
}

function PracticePage() {
  const { assignmentLangMenuOpenFor, setAssignmentLangMenuOpenFor, downloadAssignmentWorksheet } =
    useWorksheetStore();
  const { parentLinks } = useLinksStore();
  const approvedLinks = parentLinks.filter((l) => l.status === 'approved');
  const locale = useLanguage();
  const { t } = useTranslation('work');

  // Deep link (brief): `tab=assigned` opens the Assigned tab, `child=<link_id>`
  // opens and scrolls to that child's section. Both are derived from
  // the search params on EVERY render, not just on mount — the rail card lives on
  // this same route (WP3), so clicking a different child there only changes
  // the query string, and the page has to react to that rather than only to
  // its own first mount. Switching tabs still writes back to the URL
  // (replace, not push).
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const activeTab: 'Chapters' | 'Assigned' = search.tab === 'assigned' ? 'Assigned' : 'Chapters';
  const deepLinkChildId = search.child ?? null;

  const handleCategoryChange = (cat: 'Chapters' | 'Assigned') => {
    navigate({
      to: '.',
      search: (prev) =>
        cat === 'Chapters'
          ? { ...prev, tab: undefined, child: undefined }
          : { ...prev, tab: 'assigned' },
      replace: true,
    });
  };

  const [selectedSyllabus, setSelectedSyllabus] = useState<string>('KSSM');
  const [selectedForm, setSelectedForm] = useState<string>(DEFAULT_LEVEL.KSSM);
  const [dbChapters, setDbChapters] = useState<
    { chapter_id: string; chapter_num: number; title: string; title_ms?: string; status: string }[]
  >([]);
  const [loadingChapters, setLoadingChapters] = useState(false);

  // Shared with the desktop rail's Assignment summary card (WP3) so an
  // assignment made here shows up there without the rail polling on its own
  // — `refresh()` is the same "assign → refetch" hook `refreshAssignments`
  // used to be, just lifted into a store both mount points read.
  // null = not-yet-loaded or failed — AssignSheet's already-assigned rule
  // treats null as "unknown" (rows stay selectable) rather than "none".
  const {
    assignments,
    fetched: assignmentsFetched,
    loading: loadingAssignments,
    refresh: refreshAssignments,
  } = useAssignmentsStore();

  // Single object driving AssignSheet: null means closed. No preselected
  // child from this entry point (Practice browse, not a report context).
  const [assignChapter, setAssignChapter] = useState<{
    chapter_id: string;
    chapter_title: string;
    chapter_title_ms?: string;
  } | null>(null);
  // The chapter sheet opened by tapping an already-assigned chapter row.
  const [chapterSheetChapter, setChapterSheetChapter] = useState<{
    chapter_id: string;
    chapter_num: number;
    title: string;
    title_ms?: string;
  } | null>(null);
  const [gateCode, setGateCode] = useState<string | null>(null);

  // Fetched once (not gated on the Assigned tab) so the same list doubles as
  // `existingAssignments` for AssignSheet's already-assigned rule and feeds
  // the per-chapter note on the Chapters tab.
  useEffect(() => {
    if (!assignmentsFetched && !loadingAssignments) refreshAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab !== 'Chapters') return;
    setLoadingChapters(true);
    getChapters(selectedSyllabus, selectedForm)
      .then(setDbChapters)
      .catch((err) => {
        console.error(err);
        setDbChapters([]);
      })
      .finally(() => setLoadingChapters(false));
  }, [selectedSyllabus, selectedForm, activeTab]);

  const handleSyllabusChange = (sy: string) => {
    setSelectedSyllabus(sy);
    setSelectedForm(DEFAULT_LEVEL[sy]);
  };

  const created = assignments ?? [];
  const sheetAssignment = created.find((a) => a.assignment_id === assignmentLangMenuOpenFor);

  // Stuck detection for the cream filter band below — see lib/useStuck.ts.
  // `rootRef` goes on this page's own scroll column (the nearest scrolling
  // ancestor `sticky` resolves against, per the comment above it), and
  // `sentinelRef` on the 1x1px node inside the plain `relative` wrapper
  // around the sticky band (not on the sticky element itself — see the
  // comment at that wrapper for why). The band only renders on the Chapters
  // tab, so its sentinel mounts/unmounts on every tab switch — useStuck is
  // written to cope with that (see its own comment).
  const { rootRef, sentinelRef, stuck } = useStuck<HTMLDivElement, HTMLDivElement>();

  return (
    // White page with the title in flow, per ChildrenPage / student learn.tsx.
    <div className="flex-1 flex flex-col min-h-0 bg-surface">
      <div ref={rootRef} className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-3">
        {/* `pt-5` used to live on the scroll column itself. `sticky top-0`
            on the filter band below resolves against the column's PADDING
            edge, which sits below that padding — so once the band was
            stuck, a ~20px strip of the column's own top padding sat above
            the band's opaque backdrop, and a scrolling chapter card showed
            through that strip (confirmed in the running app: the stuck
            offset landed at containerTop + 20px, never at containerTop, no
            matter what margin the sticky wrapper itself carried — Chromium
            computes the stuck position from the padding edge and doesn't
            let a negative margin on the sticky element pull it past that
            edge, unlike the `-mx-4`/`px-4` horizontal trick on the band
            itself, which works precisely because horizontal margin doesn't
            feed into a column-direction flex gap the way vertical margin
            does). The fix is to give the column zero top padding and put
            that `pt-5` here instead, on the first (non-sticky) child — same
            visual gap above the title/segmented-control row, but now the
            column's padding edge coincides with its border edge, so
            `sticky top-0` pins the band flush with the column's actual top
            and there's no padding strip left for anything to show through.
            Title + segmented control share a row from md (brief: control at
            w-80 beside the h1); phone stacks them. The row is a grid from md
            so the one TrialBanner can sit between h1 and control on phone
            (DOM order) and drop to its own row under both at md+. */}
        <div className="pt-5 flex flex-col gap-3 md:grid md:grid-cols-[1fr_20rem] md:items-center">
          <h1 className="font-display text-2xl text-content md:col-start-1 md:row-start-1">
            {t('practiceTitle')}
          </h1>

          <TrialBanner wrapperClassName="md:col-span-2 md:row-start-2" />

          {/* Segmented control — student league TabSegments recipe, with a
              solid active segment (user decision 2026-08-06). White on the
              parent `primary` is the blue override, 4.94:1 — the contrast
              caveat in the student app's orange primary doesn't apply here.
              Each segment now carries a count badge (WP1: Chapters/Assigned
              replaces New/Created — content of the Assigned tab is WP2's). */}
          <div
            role="group"
            aria-label={t('tabChapters')}
            className="flex w-full rounded-md bg-bg p-0.5 md:col-start-2 md:row-start-1"
          >
            {(['Chapters', 'Assigned'] as const).map((cat) => {
              const selected = activeTab === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => handleCategoryChange(cat)}
                  className={`min-h-11 flex-1 flex items-center justify-center gap-1.5 rounded-md text-sm font-bold transition-all cursor-pointer ${
                    selected ? 'bg-primary text-surface shadow-sm' : 'text-muted'
                  }`}
                >
                  {cat === 'Chapters' ? t('tabChapters') : t('tabAssigned')}
                  {/* Chapters only fetches on the Chapters tab, so a deep
                      link straight into Assigned would show it stuck at 0 —
                      only Assigned's count is always accurate, so only
                      Assigned carries a badge (matches the approved canvas). */}
                  {cat === 'Assigned' && (
                    <span
                      className={`rounded-pill px-1.5 py-0.5 text-2xs font-bold ${
                        selected ? 'bg-surface/25 text-surface' : 'bg-surface text-muted'
                      }`}
                    >
                      {created.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === 'Chapters' ? (
          <>
            {/* Sticky filter band: this scroll column is the page's own
                `flex-1 overflow-y-auto` region (AppShell gets `scroll={false}`
                in router.tsx precisely so this div, not AppShell's `main`, is
                the nearest scrolling ancestor `sticky` resolves against) — so
                `sticky top-0` pins the band to the top of THIS column, not
                the window, and lets the `h1`/segmented control above scroll
                away as intended. The band itself (`rounded-lg bg-bg`) is
                narrower than this column's own width once you count the
                rounded corners, so an opaque `bg-surface` matching the page
                underneath it would still leave slivers at the two top
                corners for a scrolling chapter card to show through. Rather
                than trust the corner math, this wrapper breaks out of the
                column's `px-4` with matching negative margin (`-mx-4`) and
                reapplies it as its own padding, so the opaque backdrop spans
                the column edge-to-edge — no gap beside the band for
                anything to peek through, at any width, corners included.
                (The vertical equivalent of this same padding leak — the
                column's `pt-5` — can't be closed the same way here; see the
                comment on the column's first child above for why that fix
                moved there instead of onto this wrapper.)
                z-30: above the plain chapter cards below (unstyled, default
                stacking) and above Nav's z-10 raised disc, but under
                SheetDialog/SideDrawer's z-60 (AssignSheet, ChapterSheet,
                GateNoticeModal, the BottomSheet PickerField opens below `lg`)
                so a sheet still covers it. PickerField's own `lg` dropdown
                (z-20, absolute inside the band) still renders above the
                band and the cards regardless of this z-30 vs. its z-20 —
                it's a descendant of the band's own stacking context, painted
                after the band's other content within that same context, not
                competing with cards on the page's root stacking order. */}
            {/* useStuck sentinel: a sibling BEFORE the sticky div, not its
                parent — wrapping the sticky div in this wrapper made the
                wrapper (sized to fit its content, i.e. the sticky child
                alone) the sticky element's containing block, leaving it
                zero room to travel before hitting that block's bottom edge,
                so it unpinned on the very first pixel of scroll. As a flow
                sibling instead, this "relative h-0" wrapper doesn't touch
                the sticky div's containing block (still the full scroll
                column below), while still giving the 1x1px sentinel inside
                it something non-sticky to move through the flow with — see
                lib/useStuck.ts. */}
            <div className="relative h-0">
              <div
                ref={sentinelRef}
                aria-hidden="true"
                className="absolute left-0 top-0 h-px w-px"
              />
            </div>

            <div className="sticky top-0 z-30 -mx-4 px-4 bg-surface">
              {/* Picker fields replace both chip rows. Label sits right of
                    the fields, bottom-aligned, from md. Wrapped in a cream
                    inset band (`bg` = "inset row", DESIGN.md 140) so the white
                    PickerField buttons read against cream instead of
                    disappearing into the white page; the column's own gap-3
                    keeps this band clear of the segmented control's own bg-bg
                    track above it. */}
              <div
                className={`bg-bg p-3 flex flex-col gap-2 transition-all md:flex-row md:items-end md:justify-between md:gap-3 ${
                  stuck ? 'rounded-none' : 'rounded-lg'
                }`}
              >
                <div className="flex gap-2 md:max-w-[440px] md:flex-1">
                  <PickerField
                    caption={t('syllabusFieldLabel')}
                    displayValue={selectedSyllabus}
                    value={selectedSyllabus}
                    options={SYLLABUSES.map((sy) => ({ value: sy, label: sy }))}
                    onChange={handleSyllabusChange}
                    sheetTitle={t('syllabusFieldLabel')}
                    sheetSubtitle={t('chooseSyllabus')}
                  />
                  <PickerField
                    caption={t('levelFieldLabel')}
                    displayValue={levelLabel(selectedForm, locale)}
                    value={selectedForm}
                    options={levelsFor(selectedSyllabus).map((f) => ({
                      value: f,
                      label: levelLabel(f, locale),
                    }))}
                    onChange={setSelectedForm}
                    sheetTitle={t('levelFieldLabel')}
                    sheetSubtitle={t('chooseLevel')}
                  />
                </div>

                <p className="text-xs font-bold text-muted uppercase tracking-wide whitespace-nowrap">
                  {t('chapterCount', { n: dbChapters.length })}
                </p>
              </div>
            </div>

            {loadingChapters ? (
              <Spinner />
            ) : (
              // One tree: the same card at every width. Phone is one column;
              // from md the two halves (first ceil(n/2) chapters, then the rest)
              // sit side by side as two columns of cards, reading top-to-bottom
              // then left-to-right.
              <div className="flex flex-col gap-2 md:grid md:grid-cols-2 md:gap-3 md:items-start">
                {[
                  dbChapters.slice(0, Math.ceil(dbChapters.length / 2)),
                  dbChapters.slice(Math.ceil(dbChapters.length / 2)),
                ].map((group, gi) => (
                  <div key={gi} className="flex flex-col gap-2 md:gap-3">
                    {group.map((ch) => {
                      const chapterAssignments = created.filter(
                        (a) => a.chapter_id === ch.chapter_id
                      );
                      const note = assignmentNote(chapterAssignments, t, locale);
                      return (
                        <button
                          key={ch.chapter_num}
                          onClick={() =>
                            chapterAssignments.length > 0
                              ? setChapterSheetChapter({
                                  chapter_id: ch.chapter_id,
                                  chapter_num: ch.chapter_num,
                                  title: ch.title,
                                  title_ms: ch.title_ms,
                                })
                              : setAssignChapter({
                                  chapter_id: ch.chapter_id,
                                  chapter_title: ch.title,
                                  chapter_title_ms: ch.title_ms,
                                })
                          }
                          className="w-full text-left bg-surface rounded-lg p-4 border border-border flex items-center gap-3 shadow-sm transition-all hover:shadow-md active:scale-[.99]"
                        >
                          <div className="size-9 rounded-pill bg-primary/10 flex items-center justify-center text-primary font-extrabold text-sm shrink-0">
                            {ch.chapter_num}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-content leading-snug break-words">
                              {ch.title}
                            </p>
                            {note && <p className="mt-0.5 text-sm text-muted">{note}</p>}
                          </div>
                          <IconChevronRight size={16} stroke={2} className="text-hint shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : loadingAssignments ? (
          <Spinner />
        ) : (
          <AssignedByChild
            assignments={created}
            links={approvedLinks}
            locale={locale}
            openChildLinkId={deepLinkChildId}
            onDownload={(assignment) => setAssignmentLangMenuOpenFor(assignment.assignment_id)}
            onAssignAgain={(assignment) =>
              setAssignChapter({
                chapter_id: assignment.chapter_id,
                chapter_title: assignment.chapter_title,
                chapter_title_ms: assignment.chapter_title_ms ?? undefined,
              })
            }
          />
        )}
      </div>

      <AssignSheet
        open={assignChapter !== null}
        onClose={() => setAssignChapter(null)}
        chapter={assignChapter}
        existingAssignments={assignments}
        repeatWhenDone
        onAssigned={refreshAssignments}
        onGate={setGateCode}
      />

      <ChapterSheet
        open={chapterSheetChapter !== null}
        onClose={() => setChapterSheetChapter(null)}
        chapter={chapterSheetChapter}
        syllabus={selectedSyllabus}
        level={selectedForm}
        assignments={created}
        links={approvedLinks}
        onAssignAgain={(chapter) => {
          setChapterSheetChapter(null);
          setAssignChapter({
            chapter_id: chapter.chapter_id,
            chapter_title: chapter.title,
            chapter_title_ms: chapter.title_ms,
          });
        }}
        onDownload={(assignment) => setAssignmentLangMenuOpenFor(assignment.assignment_id)}
      />

      <LanguageSheet
        open={sheetAssignment !== undefined}
        onClose={() => setAssignmentLangMenuOpenFor(null)}
        title={sheetAssignment?.chapter_title ?? ''}
        onPick={(lang) =>
          sheetAssignment &&
          downloadAssignmentWorksheet(
            sheetAssignment.assignment_id,
            lang,
            sheetAssignment.chapter_id,
            sheetAssignment.link_id
          )
        }
      />

      {gateCode && <GateNoticeModal code={gateCode} onClose={() => setGateCode(null)} />}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-10">
      <div className="size-6 border-2 border-primary border-t-transparent rounded-pill animate-spin" />
    </div>
  );
}
